import { useEffect, useMemo, useState } from "react";

interface PerformancePanelProps {
  isMobile: boolean;
  impressions: number | null;
  clicks: number | null;
  gscConnected: boolean;
  top3: number;
  top10: number;
  top1120: number;
  totalRanked: number;
  visibilityScore: number | null;
  pagesThisWeek: number;
  totalPages: number;
  posts: Array<{ created_at: string }>;
}

const green = "hsl(142 70% 45%)";
const purple = "hsl(270 80% 60%)";

// Animate a number up to target
const useCountUp = (target: number, duration = 1100) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const from = 0;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const Sparkline = ({ points, color }: { points: number[]; color: string }) => {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    let raf: number;
    const tick = () => {
      setOffset((o) => (o + 0.15) % 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const w = 280;
  const h = 80;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = w / Math.max(points.length - 1, 1);

  const path = points
    .map((p, i) => {
      const x = i * step;
      const baseY = h - 6 - ((p - min) / range) * (h - 16);
      const wave = Math.sin((i + offset * 0.05) * 0.8) * 1.2;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${(baseY + wave).toFixed(2)}`;
    })
    .join(" ");

  const fillPath = `${path} L ${w} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 h-[80px] w-full">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill="url(#sparkFill)" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
    </svg>
  );
};

const PerformancePanel = ({
  isMobile,
  impressions,
  clicks,
  gscConnected,
  top3,
  top10,
  top1120,
  totalRanked,
  visibilityScore,
  pagesThisWeek,
  totalPages,
  posts,
}: PerformancePanelProps) => {
  // ---- Impressions sparkline (last 14 days from posts as proxy of activity, scaled) ----
  // We don't have daily impressions — use posts/day curve as a "live" shape, scaled to current impressions.
  const sparkPoints = useMemo(() => {
    const days = 14;
    const buckets = new Array(days).fill(0);
    const now = Date.now();
    posts.forEach((p) => {
      const d = Math.floor((now - new Date(p.created_at).getTime()) / 86400000);
      if (d >= 0 && d < days) buckets[days - 1 - d] += 1;
    });
    // Smooth + ensure non-flat
    const base = buckets.map((b, i) => b + Math.sin(i * 0.6) * 0.3 + i * 0.15 + 1);
    return base;
  }, [posts]);

  const impressionsCount = useCountUp(impressions ?? 0, 1400);
  const pagesCount = useCountUp(pagesThisWeek, 900);

  // ---- Visibility dial ----
  const score = visibilityScore ?? 0;
  const animatedScore = useCountUp(score, 1300);
  const radius = isMobile ? 48 : 72;
  const circ = 2 * Math.PI * radius;
  const dashOffset = circ - (animatedScore / 100) * circ;
  const dialSize = isMobile ? 124 : 180;

  // ---- Keyword distribution ----
  const ladderTotal = Math.max(top3 + top10 + top1120, 1);
  const top3Pct = (top3 / ladderTotal) * 100;
  const top10Pct = (top10 / ladderTotal) * 100;
  const top1120Pct = (top1120 / ladderTotal) * 100;

  // ---- Pages grid blocks ----
  const blockCount = 14;
  const filled = Math.min(pagesThisWeek, blockCount);

  return (
    <div
      className="glass relative mb-[14px] overflow-hidden rounded-[20px] p-3 md:mb-[18px] md:p-5"
      style={{
        boxShadow:
          "0 0 50px hsl(142 70% 45% / 0.06), 0 0 80px hsl(270 80% 60% / 0.05), inset 0 1px 0 hsl(0 0% 100% / 0.04)",
      }}
    >
      {/* Ambient glows */}
      <div
        className="pointer-events-none absolute -left-24 top-1/2 h-[280px] w-[280px] -translate-y-1/2 rounded-full"
        style={{ background: `radial-gradient(circle, ${green} 0%, transparent 70%)`, opacity: 0.08 }}
      />
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-[260px] w-[260px] rounded-full"
        style={{ background: `radial-gradient(circle, ${purple} 0%, transparent 70%)`, opacity: 0.1 }}
      />

      <div
        className={`relative z-10 grid gap-4 ${isMobile ? "grid-cols-1" : ""}`}
        style={!isMobile ? { gridTemplateColumns: "1.1fr 1.4fr 1.1fr" } : undefined}
      >
        {/* LEFT COLUMN — Impressions + Pages */}
        <div className="flex flex-col gap-2.5 sm:gap-3">
          {/* Impressions with sparkline */}
          <div
            data-tour-id="pp-impressions"
            className="glass glass-tinted-green relative overflow-hidden rounded-[14px] p-3"
            style={{ minHeight: isMobile ? 112 : 130 }}
          >
            <div className="text-[9.5px] font-[700] uppercase tracking-[1px] text-muted-foreground">
              Impressions · 28d
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <div
                className="text-[26px] font-[900] leading-none tracking-[-1px] text-foreground"
                style={{ textShadow: `0 0 16px ${green}` }}
              >
                {impressions === null ? "—" : impressionsCount.toLocaleString()}
              </div>
              <div className="text-[10px] font-[700]" style={{ color: green }}>
                {clicks !== null ? `${clicks.toLocaleString()} clicks` : gscConnected ? "Loading…" : "Connect GSC"}
              </div>
            </div>
            <Sparkline points={sparkPoints} color={green} />
          </div>

          {/* Pages this week */}
          <div
            data-tour-id="pp-pages"
            className="glass glass-tinted-green relative overflow-hidden rounded-[14px] p-3"
          >
            <div className="flex items-center justify-between">
              <div className="text-[9.5px] font-[700] uppercase tracking-[1px] text-muted-foreground">
                Pages · This Week
              </div>
              <div className="text-[9px] font-[700] text-muted-foreground">{totalPages} total</div>
            </div>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[10px] font-[800]" style={{ color: green }}>+</span>
              <span
                className="text-[28px] font-[900] leading-none tracking-[-1px] text-foreground"
                style={{ textShadow: `0 0 14px ${green}` }}
              >
                {pagesCount}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-7 gap-[3px]">
              {Array.from({ length: blockCount }).map((_, i) => {
                const active = i < filled;
                return (
                  <div
                    key={i}
                    className="h-[10px] rounded-[2px] transition-all"
                    style={{
                      background: active ? green : "hsl(0 0% 100% / 0.05)",
                      boxShadow: active ? `0 0 6px ${green}` : "none",
                      animation: active ? `pulse 2.4s ease-in-out ${i * 0.07}s infinite` : "none",
                      opacity: active ? 1 : 0.6,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* CENTER — Visibility Score (HERO) */}
        <div
          data-tour-id="pp-visibility"
          className="glass glass-tinted-purple relative flex flex-col items-center justify-center rounded-[14px] p-3 sm:p-4"
          style={{ minHeight: isMobile ? 184 : 270 }}
        >
          <div className="absolute inset-0 rounded-[14px] pointer-events-none" style={{
            background: `radial-gradient(circle at 50% 50%, ${purple} 0%, transparent 60%)`,
            opacity: 0.1,
            animation: "pulse 3.5s ease-in-out infinite",
          }} />

          <div className="text-[10px] font-[800] uppercase tracking-[1.4px] text-muted-foreground">
            Visibility Score
          </div>

          <div className="relative mt-2" style={{ width: dialSize, height: dialSize }}>
            <svg width={dialSize} height={dialSize} viewBox={`0 0 ${dialSize} ${dialSize}`}>
              <defs>
                <linearGradient id="dialGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={green} />
                  <stop offset="100%" stopColor={purple} />
                </linearGradient>
              </defs>
              <circle
                cx={dialSize / 2}
                cy={dialSize / 2}
                r={radius}
                fill="none"
                stroke="hsl(0 0% 100% / 0.07)"
                strokeWidth="9"
              />
              <circle
                cx={dialSize / 2}
                cy={dialSize / 2}
                r={radius}
                fill="none"
                stroke="url(#dialGrad)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${dialSize / 2} ${dialSize / 2})`}
                style={{
                  transition: "stroke-dashoffset 0.4s ease",
                  filter: `drop-shadow(0 0 10px ${purple}) drop-shadow(0 0 6px ${green})`,
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div
                className="font-[900] leading-none tracking-[-2px] text-foreground"
                style={{
                  fontSize: isMobile ? 38 : 56,
                  textShadow: `0 0 22px ${purple}, 0 0 14px ${green}`,
                  animation: "pulse 3.5s ease-in-out infinite",
                }}
              >
                {visibilityScore === null ? "—" : animatedScore}
              </div>
              <div className="mt-1 text-[8.5px] font-[800] uppercase tracking-[1.2px] text-muted-foreground">
                of 100
              </div>
            </div>
          </div>

          <div className="mt-2 text-[10px] font-[700]" style={{ color: green }}>
            {visibilityScore === null
              ? "—"
              : visibilityScore >= 80
              ? "Excellent · Site healthy"
              : visibilityScore >= 60
              ? "Good · Improving"
              : "Needs attention"}
          </div>
        </div>

        {/* RIGHT — Keywords Top 10 ladder */}
        <div
          data-tour-id="pp-keywords"
          className="glass glass-tinted-purple relative overflow-hidden rounded-[14px] p-3"
          style={{ minHeight: isMobile ? 140 : 270 }}
        >
          <div className="flex items-center justify-between">
            <div className="text-[9.5px] font-[700] uppercase tracking-[1px] text-muted-foreground">
              Keyword Ladder
            </div>
            <div className="text-[9px] font-[700] text-muted-foreground">{totalRanked} tracked</div>
          </div>

          <div className="mt-2 flex items-baseline gap-2">
            <div
              className="text-[28px] font-[900] leading-none tracking-[-1px] text-foreground"
              style={{ textShadow: `0 0 14px ${purple}` }}
            >
              {top10}
            </div>
            <div className="text-[10px] font-[700]" style={{ color: purple }}>in Top 10</div>
          </div>

          <div className="mt-3 flex flex-col gap-[10px]">
            {[
              { label: "Top 3", count: top3, pct: top3Pct, color: "hsl(45 95% 55%)" },
              { label: "Top 10", count: top10, pct: top10Pct, color: green },
              { label: "11–20", count: top1120, pct: top1120Pct, color: purple },
            ].map((row) => (
              <div key={row.label}>
                <div className="mb-[3px] flex items-center justify-between text-[9.5px] font-[700]">
                  <span style={{ color: row.color }}>{row.label}</span>
                  <span className="text-muted-foreground">{row.count}</span>
                </div>
                <div className="h-[6px] w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${row.pct}%`,
                      background: `linear-gradient(90deg, ${row.color}, ${row.color === green ? purple : row.color})`,
                      boxShadow: `0 0 8px ${row.color}`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformancePanel;
